/**
 * audioCapture.js — the brain of the recorder's audio pipeline.
 *
 * Three capture modes, returned as a single unified MediaStream that
 * SessionRecording.js can feed straight into MediaRecorder:
 *
 *   1) "in_person"      — one person in the room, mic-only, default
 *                         browser processing (echo cancel + AGC on).
 *                         Best for an office session.
 *
 *   2) "mic_only"       — telehealth fallback. Mic only, but ALL
 *                         browser processing disabled so the far-end
 *                         voice coming from the provider's speakers
 *                         is not treated as "echo" and deleted. Higher
 *                         sample rate. Works on every browser, every
 *                         meeting platform — the provider just needs
 *                         headphones (we warn them in the UI).
 *
 *   3) "mic_plus_tab"   — telehealth best-quality. Captures audio from
 *                         a shared browser tab (the Zoom/Meet/Doxy tab)
 *                         via getDisplayMedia + the mic via getUserMedia,
 *                         mixes them with Web Audio API, returns a
 *                         single MediaStream. Patient audio is captured
 *                         *digitally* from the meeting tab — no mic
 *                         round-trip, no echo, no background noise.
 *                         Chrome/Edge have full support; Safari is
 *                         limited and we detect that in canCaptureTab().
 */

const IN_PERSON_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 16000,
  },
};

// Telehealth — browser processing OFF so remote speaker isn't
// suppressed as "echo". 48 kHz → Whisper loves higher-rate audio.
const TELEHEALTH_MIC_CONSTRAINTS = {
  audio: {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
    sampleRate: 48000,
    channelCount: 1,
  },
};

export function canCaptureTab() {
  return !!(navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function');
}

/**
 * Best-effort meeting-platform detection from the shared tab's
 * video-track label. Chrome/Edge populate the track label with the
 * page title (e.g. "Zoom Meeting - Chrome") or the tab URL fragment
 * ("tab://meet.google.com/xyz-abc-def"). Safari labels are opaque.
 *
 * Returns one of: "zoom" | "google_meet" | "microsoft_teams" |
 * "doxy" | "simplepractice" | "webex" | "whereby" | "ringcentral" |
 * "gotomeeting" | "jitsi" | "unknown".
 */
export function detectMeetingPlatform(trackLabel) {
  if (!trackLabel || typeof trackLabel !== 'string') return 'unknown';
  const l = trackLabel.toLowerCase();
  if (/\bzoom(\.us)?\b|zoom meeting|zoom workplace/.test(l)) return 'zoom';
  if (/meet\.google\.com|google meet\b/.test(l)) return 'google_meet';
  if (/teams\.microsoft\.com|microsoft teams\b/.test(l)) return 'microsoft_teams';
  if (/doxy\.me|doxy\b/.test(l)) return 'doxy';
  if (/simplepractice\.com|simplepractice\b/.test(l)) return 'simplepractice';
  if (/webex\.com|webex\b/.test(l)) return 'webex';
  if (/whereby\.com|whereby\b/.test(l)) return 'whereby';
  if (/ringcentral|rcv\.ringcentral/.test(l)) return 'ringcentral';
  if (/gotomeeting|goto meeting|goto\.com/.test(l)) return 'gotomeeting';
  if (/jitsi|meet\.jit\.si/.test(l)) return 'jitsi';
  return 'unknown';
}

/** Human-friendly label for a platform id. */
export function meetingPlatformLabel(id) {
  switch (id) {
    case 'zoom': return 'Zoom';
    case 'google_meet': return 'Google Meet';
    case 'microsoft_teams': return 'Microsoft Teams';
    case 'doxy': return 'Doxy.me';
    case 'simplepractice': return 'SimplePractice';
    case 'webex': return 'Webex';
    case 'whereby': return 'Whereby';
    case 'ringcentral': return 'RingCentral';
    case 'gotomeeting': return 'GoToMeeting';
    case 'jitsi': return 'Jitsi';
    default: return 'Meeting';
  }
}

/**
 * Returns a MediaStream ready for MediaRecorder plus a cleanup fn
 * that must be called when recording stops (kills all underlying
 * tracks and closes the AudioContext so the browser doesn't hold
 * the mic/tab-share indicator on forever).
 *
 *   const { stream, cleanup, capture_method, warnings } = await captureAudio('telehealth', 'mic_plus_tab');
 */
export async function captureAudio(sessionMode, preferredMethod) {
  const mode = sessionMode === 'telehealth' ? 'telehealth' : 'in_person';
  const warnings = [];

  if (mode === 'in_person') {
    const mic = await navigator.mediaDevices.getUserMedia(IN_PERSON_CONSTRAINTS);
    return {
      stream: mic,
      cleanup: () => mic.getTracks().forEach((t) => t.stop()),
      capture_method: 'mic_only',
      meeting_platform: null,
      warnings,
    };
  }

  // Telehealth branch ------------------------------------------------
  if (preferredMethod === 'mic_plus_tab' && canCaptureTab()) {
    try {
      return await _captureMicPlusTab(warnings);
    } catch (err) {
      // Degrade gracefully: user cancelled the tab picker, or browser
      // refused, or tab had no audio. Fall back to mic-only rather
      // than breaking the session.
      warnings.push(_explainTabCaptureFailure(err));
      // fall through
    }
  }

  // Telehealth mic-only fallback
  const mic = await navigator.mediaDevices.getUserMedia(TELEHEALTH_MIC_CONSTRAINTS);
  return {
    stream: mic,
    cleanup: () => mic.getTracks().forEach((t) => t.stop()),
    capture_method: 'mic_only',
    meeting_platform: null,
    warnings,
  };
}

// ---- internals ----------------------------------------------------
async function _captureMicPlusTab(warnings) {
  // Ask for a tab share WITH audio. We require video to be chosen too
  // because Chrome only enables `audio` on tab picks (not on the whole
  // screen + window share options). We throw away the video track.
  const display = await navigator.mediaDevices.getDisplayMedia({
    video: { width: { max: 640 }, frameRate: { max: 1 } }, // cheapest dummy video
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    },
    // Chrome hints — ignored by other browsers
    systemAudio: 'include',
    selfBrowserSurface: 'exclude',
    surfaceSwitching: 'include',
    preferCurrentTab: false,
  });

  const tabAudioTracks = display.getAudioTracks();
  if (tabAudioTracks.length === 0) {
    // User picked a tab/window but forgot to tick "Share tab audio".
    display.getTracks().forEach((t) => t.stop());
    const e = new Error('NO_TAB_AUDIO');
    e.code = 'NO_TAB_AUDIO';
    throw e;
  }

  // Try to identify the meeting platform from the video-track label.
  // In Chrome/Edge this is the shared tab's title (e.g. "Zoom
  // Meeting - Chrome"). Best-effort only — unknown on Safari.
  const videoTrack = display.getVideoTracks()[0];
  const rawLabel = (videoTrack && videoTrack.label) || '';
  const meeting_platform = detectMeetingPlatform(rawLabel);

  // Grab the mic next (so the tab-picker is the FIRST permission prompt
  // the user sees — less confusing UX).
  const mic = await navigator.mediaDevices.getUserMedia(TELEHEALTH_MIC_CONSTRAINTS);

  // Mix via Web Audio: both sources → one destination stream.
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const ctx = new AudioCtx();
  const dest = ctx.createMediaStreamDestination();

  const micSrc = ctx.createMediaStreamSource(mic);
  const tabSrc = ctx.createMediaStreamSource(new MediaStream(tabAudioTracks));

  // A little gain on the mic so the provider is never drowned out
  // by a loud meeting. Tab audio stays at unity.
  const micGain = ctx.createGain();
  micGain.gain.value = 1.2;

  micSrc.connect(micGain).connect(dest);
  tabSrc.connect(dest);

  // If the user clicks "Stop sharing" in the Chrome chip, we fall
  // back to mic-only automatically rather than dying mid-session.
  const tabTrack = tabAudioTracks[0];
  tabTrack.addEventListener('ended', () => {
    try { tabSrc.disconnect(); } catch {}
    warnings.push('Tab audio sharing was stopped. Continuing on mic only.');
  });

  const cleanup = () => {
    try { mic.getTracks().forEach((t) => t.stop()); } catch {}
    try { display.getTracks().forEach((t) => t.stop()); } catch {}
    try { ctx.close(); } catch {}
  };

  return {
    stream: dest.stream,
    cleanup,
    capture_method: 'mic_plus_tab',
    meeting_platform,
    warnings,
  };
}

function _explainTabCaptureFailure(err) {
  const name = err && err.name;
  if (err && err.code === 'NO_TAB_AUDIO') {
    return 'Tab audio wasn\'t shared. Tick "Share tab audio" in the picker next time, or use mic-only mode.';
  }
  if (name === 'NotAllowedError') return 'Tab sharing was cancelled. Falling back to mic-only mode.';
  if (name === 'NotFoundError') return 'No sharable tab was available. Falling back to mic-only mode.';
  if (name === 'AbortError') return 'Tab picker was dismissed. Falling back to mic-only mode.';
  return 'Could not capture tab audio — falling back to mic-only mode.';
}
